<script setup>
import { onBeforeUnmount, onMounted, ref } from 'vue'

defineProps({
    data: {
        type: Object,
        default: () => ({}),
    },
})

const emit = defineEmits(['close'])

const root = ref(null)

// Held onto rather than re-read on the way out: the template ref is already null by
// `onBeforeUnmount`, so `root.value.parentElement` would throw and leave the wrapper pinned.
let modalWrapper = null

onMounted(() => {
    modalWrapper = root.value.parentElement
    modalWrapper.style.alignSelf = 'flex-start'
    modalWrapper.style.marginTop = '5px'
})

onBeforeUnmount(() => {
    modalWrapper.style.removeProperty('align-self')
    modalWrapper.style.removeProperty('margin-top')
})

function close() {
    emit('close')
}
</script>

<template>
    <div
        ref="root"
        class="in-app-browser-modal"
    >
        <BaseIcon
            name="arrow-down"
            :size="32"
            class="arrow"
            aria-hidden="true"
        />
        <p
            class="headline"
            data-test="headline"
        >
            Looks like you're on Instagram or Facebook. To go straight to the content shared in-app:
        </p>

        <div class="steps">
            <div
                class="step"
                data-test="step"
            >
                <span
                    class="step-number"
                    data-test="step-number"
                >1</span>
                <p class="step-text">
                    Tap the <strong>3 dots (...)</strong> on the upper right.
                </p>
            </div>

            <div
                class="step"
                data-test="step"
            >
                <span
                    class="step-number"
                    data-test="step-number"
                >2</span>
                <p class="step-text">
                    Tap <strong>Open in external browser</strong>
                </p>
            </div>
        </div>

        <button
            class="dismiss"
            data-test="dismiss"
            @click="close"
        >
            Dismiss
        </button>
    </div>
</template>

<style scoped lang="scss">
@use '@design' as *;

.in-app-browser-modal {
    position: relative;
    width: calc(100vw - #{$s-xxl});
    max-width: 420px;
    padding: $s-xxl $s-xl $s-xl;
    overflow: visible;
    border: none;
    border-radius: $radius-16;

    @include elevationDialog;
}

.arrow {
    position: absolute;
    top: 10px;
    right: 16px;
    color: $c-accent-default;
    pointer-events: none;
}

.headline {
    padding-right: 70px;
    margin: 0 0 $s-xl 0;
    font-size: 18px;
    font-weight: 500;
    line-height: 1.45;
    color: $c-white;
    letter-spacing: -0.36px;
}

.steps {
    display: flex;
    flex-direction: column;
    gap: $s-m;
    margin-bottom: $s-xl;
}

.step {
    display: flex;
    gap: $s-m;
    align-items: center;
}

.step-number {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    font-size: 18px;
    font-weight: 500;
    line-height: 1.45;
    color: $c-white;
    letter-spacing: -0.36px;
    background: $c-black;
    border-radius: 50%;
}

.step-text {
    margin: 0;
    font-size: 14px;
    font-weight: 500;
    line-height: 1.45;
    color: $c-white;
    letter-spacing: -0.28px;

    strong {
        font-weight: 700;
    }
}

.dismiss {
    display: block;
    width: 100%;
    padding: $s-xs 0;
    font-size: 13px;
    font-weight: 700;
    line-height: 1.45;
    color: $c-white;
    text-align: center;
    letter-spacing: -0.26px;
    cursor: pointer;
    background: none;
    border: none;

    &:active {
        opacity: 0.7;
    }
}
</style>
